class ProductComposer extends HTMLElement {
  constructor() {
    super();
    this.config = JSON.parse(this.querySelector('[data-product-composer-json]').textContent);
    this.separateMode = Boolean(this.querySelector('[data-linked-flavors-json]'));
    if (this.separateMode) this.configureLinkedProducts();
    this.flavorIndex = Number(this.dataset.flavorOptionIndex);
    this.addLabel = this.dataset.addLabel || 'Add to bag';
    this.preAddLabel = this.dataset.preAddLabel || this.addLabel;
    this.purchaseHelperText = this.dataset.purchaseHelper || 'Add at least 1 item above to continue.';
    this.helperTimer = null;
    this.servings = Number(this.dataset.servings || 0);
    this.unitSingular = this.dataset.unitSingular || 'product';
    this.unitPlural = this.dataset.unitPlural || 'products';
    this.tierTwo = Number(this.dataset.tierTwo || 2);
    this.tierThree = Number(this.dataset.tierThree || 3);
    this.tierTwoDiscount = Number(this.dataset.tierTwoDiscount || 0);
    this.tierThreeDiscount = Number(this.dataset.tierThreeDiscount || 0);
    this.tierLabels = [this.dataset.tierOneLabel || 'Try it', this.dataset.tierTwoLabel || 'Monthly routine', this.dataset.tierThreeLabel || 'Stocked up'];
    this.quantities = Object.create(null);
    this.optionSelections = {};
    this.activeFlavor = '';
    this.purchaseType = this.config.requiresSellingPlan ? 'subscription' : 'onetime';
    this.planId = '';
    if (this.dataset.purchaseMode !== 'app') this.init();
  }

  connectedCallback() {
    this.factsController?.abort();
    this.factsController = new AbortController();
    document.addEventListener('bellyful:facts-loaded', () => this.syncNutrition(), {signal:this.factsController.signal});
    this.syncNutrition();
    window.BellyfulFacts?.load();
  }

  disconnectedCallback() { this.factsController?.abort(); }

  configureLinkedProducts() {
    const variants = JSON.parse(this.querySelector('[data-linked-flavors-json]').textContent);
    const labels = new Set(), ids = new Set();
    this.connectionError = variants.some(v => {
      const label = v.options[0].trim().toLowerCase();
      const duplicate = !label || labels.has(label) || ids.has(String(v.id));
      labels.add(label); ids.add(String(v.id)); return duplicate;
    });
    // Resolve facts from the same registry used by the nutrition drawer.
    // Explicit connected-flavor values win; missing fields inherit, never invent.
    const profiles = new Map();
    document.querySelectorAll('[data-nutrition-data]').forEach(node => {
      try { JSON.parse(node.textContent).forEach(profile => profiles.set(profile.key, profile)); } catch (_) { /* An invalid profile must not block shopping. */ }
    });
    variants.forEach(v => {
      const profile = v.nutritionProfile || profiles.get(v.nutritionKey);
      if (profile) {
        if (!(v.servings > 0)) v.servings = Number(profile.servings) || 0;
        const names = ['protein', 'calories', 'total sugars'];
        v.nutrition.highlights.forEach((item, i) => {
          if (item.value !== null && item.value !== undefined && String(item.value).trim() !== '') return;
          const row = (profile.rows || []).find(row => String(row.l).trim().toLowerCase() === names[i]);
          if (row) item.value = String(row.amt).replace(/\s+/g, '');
        });
      }
      const mappings = new Map();
      String(v.planMap || '').split(/\r?\n/).filter(line => line.trim()).forEach(line => {
        const [key, id, ...label] = line.split('|').map(value => value.trim());
        if (key && /^\d+$/.test(id) && label.join('|')) mappings.set(id, {key:'mapped:'+key, label:label.join('|')});
      });
      v.sellingPlans = v.sellingPlans.map(p => ({...p, realId:p.id, id:mappings.get(String(p.id))?.key || String(p.id), label:mappings.get(String(p.id))?.label || p.name}));
      // An ambiguous mapping must never silently choose one of two allocations.
      if (new Set(v.sellingPlans.map(p => p.id)).size !== v.sellingPlans.length) this.connectionError = true;
    });
    this.config.variants = variants;
    this.config.selectedVariantId = (variants.find(v => String(v.productId) === this.dataset.productId) || variants[0])?.id;
    this.config.requiresSellingPlan = false;
    const pills = this.querySelector('[data-frequency-pills]');
    if (pills) {
      pills.replaceChildren(); const seen = new Set();
      variants.forEach(v => v.sellingPlans.forEach(p => {
        if (seen.has(String(p.id))) return; seen.add(String(p.id));
        const button = document.createElement('button'); button.type = 'button';
        button.dataset.sellingPlanId = String(p.id); button.dataset.deliveryLabel = p.label;
        button.textContent = p.label; pills.append(button);
      }));
    }
  }

  init() {
    this.querySelectorAll('[data-flavor-row]').forEach((row, index) => {
      const flavor = row.dataset.flavor;
      this.quantities[flavor] = 0;
      if (index === 0) this.activeFlavor = flavor;
    });

    const selected = this.config.variants.find((variant) => String(variant.id) === String(this.config.selectedVariantId));
    if (selected && this.flavorIndex >= 0) {
      this.activeFlavor = selected.options[this.flavorIndex];
    } else if (this.flavorIndex < 0 && this.activeFlavor) {
      this.quantities[this.activeFlavor] = 1;
    }

    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const pressed = group.querySelector('[data-option-value][aria-pressed="true"]') || group.querySelector('[data-option-value]');
      if (pressed) this.optionSelections[index] = pressed.dataset.optionValue;
    });

    if (this.separateMode) {
      if (this.connectionError) {
        const message = this.querySelector('[data-composer-message]');
        if (message) message.textContent = 'Flavor connections need review: use unique flavor labels, products and plan mappings.';
      }
    }
    this.bind();
    this.sync();
  }

  bind() {
    this.addEventListener('click', (event) => {
      const plus = event.target.closest('[data-qty-plus]');
      if (plus) return this.changeQty(plus.dataset.qtyPlus, 1);

      const minus = event.target.closest('[data-qty-minus]');
      if (minus) return this.changeQty(minus.dataset.qtyMinus, -1);

      const view = event.target.closest('[data-flavor-view]');
      if (view) {
        this.activeFlavor = view.dataset.flavorView;
        this.sync();
        return;
      }

      const option = event.target.closest('[data-option-value]');
      if (option) {
        const group = option.closest('[data-option-index]');
        group.querySelectorAll('[data-option-value]').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
        this.optionSelections[Number(group.dataset.optionIndex)] = option.dataset.optionValue;
        this.sync();
        return;
      }

      const tier = event.target.closest('[data-tier-quantity]');
      if (tier) {
        this.setTierQuantity(Number(tier.dataset.tierQuantity));
        return;
      }

      const plan = event.target.closest('[data-selling-plan-id]');
      if (plan) {
        this.planId = String(plan.dataset.sellingPlanId);
        this.purchaseType = 'subscription';
        this.sync();
        return;
      }

      const purchaseOption = event.target.closest('[data-purchase-option]');
      if (purchaseOption && this.totalQty() <= 0) {
        event.preventDefault();
        this.highlightPurchaseHelper();
        return;
      }

      const add = event.target.closest('[data-composer-add], [data-mobile-add]');
      if (add) this.addToCart(add);
    });

    this.querySelectorAll('[name^="purchase_type_"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.purchaseType = radio.value;
        if (this.purchaseType === 'subscription' && !this.planId) this.planId = this.firstCommonPlanId();
        this.sync();
      });
    });
  }

  changeQty(flavor, delta) {
    const row = this.querySelector(`[data-flavor-row][data-flavor="${CSS.escape(flavor)}"]`);
    if (delta > 0 && row?.dataset.available === 'false') return;

    this.activeFlavor = flavor;
    this.quantities[flavor] = Math.max(0, (this.quantities[flavor] || 0) + delta);

    if (this.totalQty() === 0 && this.flavorIndex < 0) {
      this.quantities[flavor] = 1;
    } else if ((this.quantities[flavor] || 0) === 0 && this.totalQty() > 0) {
      const remaining = Object.entries(this.quantities).find(([, quantity]) => quantity > 0);
      if (remaining) this.activeFlavor = remaining[0];
    }

    this.sync();
    if (delta > 0) this.showSelectionMessage();
    else {
      const message = this.querySelector('[data-selection-message]');
      if (message) { message.hidden = true; message.classList.remove('is-visible'); }
    }
  }

  totalQty() {
    return Object.values(this.quantities).reduce((total, quantity) => total + quantity, 0);
  }

  setTierQuantity(target) {
    if (!Number.isFinite(target) || target < 1) return;
    let difference = target - this.totalQty();
    if (difference > 0) {
      this.quantities[this.activeFlavor] = (this.quantities[this.activeFlavor] || 0) + difference;
    } else if (difference < 0) {
      let remaining = Math.abs(difference);
      const order = [this.activeFlavor, ...Object.keys(this.quantities).filter((key) => key !== this.activeFlavor)];
      for (const key of order) {
        const removable = Math.min(this.quantities[key] || 0, remaining);
        this.quantities[key] = (this.quantities[key] || 0) - removable;
        remaining -= removable;
        if (!remaining) break;
      }
    }
    if (this.totalQty() === 0 && this.flavorIndex < 0) this.quantities[this.activeFlavor] = 1;
    this.sync();
    if (difference > 0) this.showSelectionMessage();
    else { const message = this.querySelector('[data-selection-message]'); if (message) message.hidden = true; }
  }

  quantityDiscount(quantity = this.totalQty(), subscription = this.purchaseType === 'subscription') {
    if (this.separateMode && ((this.dataset.linkedDiscountScope || 'off') === 'off' || (subscription && this.dataset.linkedDiscountSubscriptions !== 'true'))) return 0;
    const percent = quantity >= this.tierThree ? this.tierThreeDiscount : quantity >= this.tierTwo ? this.tierTwoDiscount : 0;
    return Math.max(0, Math.min(100, Number(percent) || 0));
  }

  applyQuantityDiscount(cents, quantity = this.totalQty(), subscription = this.purchaseType === 'subscription') {
    return Math.round(cents * (100 - this.quantityDiscount(quantity, subscription)) / 100);
  }

  // Every price preview uses this calculation with an explicit purchase type.
  priceForLines(lines, subscription = false) {
    const count = lines.reduce((n, line) => n + line.quantity, 0);
    return lines.reduce((sum, line) => {
      const allocation = subscription && this.planId ? this.allocation(line.variant) : null;
      const unit = allocation ? allocation.price : line.variant.price;
      const qualifying = this.separateMode && this.dataset.linkedDiscountScope === 'same' ? line.quantity : count;
      return sum + this.applyQuantityDiscount(unit * line.quantity, qualifying, subscription);
    }, 0);
  }

  tierLines(target) {
    const quantities = {...this.quantities};
    let difference = target - this.totalQty();
    if (difference > 0) quantities[this.activeFlavor] = (quantities[this.activeFlavor] || 0) + difference;
    else for (const flavor of [this.activeFlavor, ...Object.keys(quantities).filter(k => k !== this.activeFlavor)]) {
      const remove = Math.min(quantities[flavor] || 0, -difference);
      quantities[flavor] = (quantities[flavor] || 0) - remove; difference += remove;
      if (difference >= 0) break;
    }
    return Object.entries(quantities).filter(([,q]) => q > 0).map(([flavor, quantity]) => ({flavor, quantity, variant:this.selectedVariantForFlavor(flavor)})).filter(line => line.variant);
  }

  selectedVariantForFlavor(flavor) {
    if (this.flavorIndex < 0) {
      return this.config.variants.find((variant) => variant.available && variant.options.every((value, index) => !this.optionSelections[index] || value === this.optionSelections[index]))
;
    }

    return this.config.variants.find((variant) => variant.available && variant.options.every((value, index) => {
      if (index === this.flavorIndex) return value === flavor;
      return !this.optionSelections[index] || value === this.optionSelections[index];
    }));
  }

  selectedLines() {
    return Object.entries(this.quantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([flavor, quantity]) => ({ flavor, quantity, variant: this.selectedVariantForFlavor(flavor) }))
      .filter((line) => line.variant);
  }

  commonPlans() {
    const lines = this.selectedLines();
    if (!lines.length) return [];
    let ids = lines[0].variant.sellingPlans.map((plan) => String(plan.id));
    for (const line of lines.slice(1)) {
      const next = new Set(line.variant.sellingPlans.map((plan) => String(plan.id)));
      ids = ids.filter((id) => next.has(id));
    }
    const displayed = new Set([...this.querySelectorAll('[data-selling-plan-id]')].map(button => String(button.dataset.sellingPlanId)));
    return ids.filter(id => displayed.has(id));
  }

  firstCommonPlanId() {
    return this.commonPlans()[0] || '';
  }

  allocation(variant) {
    return variant.sellingPlans.find((plan) => String(plan.id) === String(this.planId));
  }

  linePrice(line) {
    if (this.purchaseType === 'subscription' && this.planId) {
      const allocation = this.allocation(line.variant);
      if (allocation) return allocation.price;
    }
    return line.variant.price;
  }

  linkedTotal(subscription = false) { return this.priceForLines(this.selectedLines(), subscription); }

  totalPrice() { return this.priceForLines(this.selectedLines(), this.purchaseType === 'subscription'); }

  oneTimeTotal() { return this.priceForLines(this.selectedLines(), false); }

  format(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  sync() {
    if (this.separateMode) {
      this.config.requiresSellingPlan = this.selectedLines().some(line => line.variant.requiresSellingPlan);
      if (this.config.requiresSellingPlan) this.purchaseType = 'subscription';
    }
    const common = this.commonPlans();
    if (!common.includes(String(this.planId))) this.planId = common[0] || '';
    if (!this.planId && !this.config.requiresSellingPlan) this.purchaseType = 'onetime';

    this.syncRows();
    this.syncMedia();
    this.syncDynamicTiles();
    this.syncNutrition();
    this.syncSummary();
    this.syncTierCards();
    this.syncPurchase();
    this.syncAddState();
  }

  syncTierCards() {
    const quantity = this.totalQty();
    const activeTier = quantity === 0 ? 0 : quantity >= this.tierThree ? this.tierThree : quantity >= this.tierTwo ? this.tierTwo : 1;
    this.querySelectorAll('[data-tier-quantity]').forEach((button) => {
      const target = Number(button.dataset.tierQuantity);
      button.setAttribute('aria-pressed', String(target === activeTier));
      const price = button.querySelector('[data-tier-price]');
      if (!price) return;
      const lines = this.tierLines(target);
      if (!lines.length) { price.textContent = ''; return; }
      const subscription = this.purchaseType === 'subscription';
      price.textContent = this.format(this.priceForLines(lines, subscription));
      const servings = lines.every(line => (this.separateMode ? line.variant.servings : this.servings) > 0)
        ? lines.reduce((n,line) => n + line.quantity * (this.separateMode ? line.variant.servings : this.servings),0) : 0;
      const small = button.querySelector('small');
      if (small) small.textContent = `${target}${target === this.tierThree ? '+' : ''} ${target === 1 ? this.unitSingular : this.unitPlural}${servings > 0 ? ` · ${servings}${target === this.tierThree ? '+' : ''} servings` : ''}`;
      let badge = button.querySelector('[data-tier-saving]') || button.querySelector('span');
      if (!badge) { badge = document.createElement('span'); button.append(badge); }
      badge.dataset.tierSaving = '';
      const rates = lines.map(line => this.quantityDiscount(this.separateMode && this.dataset.linkedDiscountScope === 'same' ? line.quantity : target, subscription));
      const discount = Math.max(...rates);
      badge.hidden = discount <= 0 || this.dataset.showQuantitySavings === 'false';
      badge.textContent = discount > 0 ? `${rates.some(rate => rate !== discount) ? 'Up to ' : ''}${(this.dataset.quantitySavingLabel || '{percent}% quantity savings').replace('{percent}', discount)}${this.separateMode && this.dataset.linkedDiscountScope === 'same' ? ' · same flavor' : ''}` : '';
    });
  }

  syncRows() {
    this.querySelectorAll('[data-flavor-row]').forEach((row) => {
      const flavor = row.dataset.flavor;
      const variant = this.selectedVariantForFlavor(flavor);
      const available = Boolean(variant);
      row.dataset.available = String(available);
      row.classList.toggle('is-active', flavor === this.activeFlavor);
      row.classList.toggle('has-quantity', (this.quantities[flavor] || 0) > 0);
      row.classList.toggle('is-unavailable', !available);
      const output = row.querySelector('[data-qty-output]');
      const plus = row.querySelector('[data-qty-plus]');
      if (output) output.value = this.quantities[flavor] || 0;
      if (plus) plus.disabled = !available;
    });

    const active = this.querySelector('[data-active-flavor]');
    if (active) active.textContent = this.activeFlavor === '__single__' ? '' : this.activeFlavor;
    const selectionMessage = this.querySelector('[data-selection-message]');
    if (selectionMessage && (this.quantities[this.activeFlavor] || 0) <= 0) {
      selectionMessage.hidden = true;
      selectionMessage.classList.remove('is-visible');
    }
  }

  highlightPurchaseHelper() {
    const helper = this.querySelector('[data-purchase-helper]');
    if (!helper) return;
    helper.textContent = this.purchaseHelperText;
    helper.classList.add('is-highlighted');
    clearTimeout(this.helperTimer);
    this.helperTimer = setTimeout(() => helper.classList.remove('is-highlighted'), 1400);
  }

  showSelectionMessage() {
    const message = this.querySelector('[data-selection-message]');
    if (!message) return;
    const variant = this.selectedVariantForFlavor(this.activeFlavor);
    const quantity = this.quantities[this.activeFlavor] || 0;
    const total = this.totalQty();
    if (!quantity || !total) { message.hidden = true; return; }
    const flavor = this.activeFlavor === '__single__' ? (this.config.title || 'Your pick') : this.activeFlavor;
    const fallback = this.dataset.selectionMessageFallback || 'That’s a good call ;b';
    let text = variant?.selectionMessage || `${flavor}. ${fallback}`;
    if (this.dataset.dynamicMessages === 'true') {
      const count = Object.values(this.quantities).filter(value => value > 0).length;
      const custom = (this.config.flavorMessages || []).find(item => item.flavor?.trim().toLowerCase() === flavor.trim().toLowerCase()) || {};
      let template;
      if (count >= 3) template = this.dataset.messageVariety;
      else if (count === 2 && quantity === 1) template = this.dataset.messageMixed;
      else if (quantity >= 3) template = custom.stocked || this.dataset.messageStocked;
      else if (quantity === 2) template = custom.repeat || this.dataset.messageRepeat;
      else template = custom.first || variant?.selectionMessage || this.dataset.messageFirst;
      const tokens = {flavor, quantity, total, unit: this.unitSingular, units: quantity === 1 ? this.unitSingular : this.unitPlural, flavors_count: count};
      text = (template || text).replace(/\{(flavor|quantity|total|unit|units|flavors_count)\}/g, (_, key) => String(tokens[key]));
    }
    // Update one polite status message; never restart an animation on each click.
    if (message.textContent !== text) message.textContent = text;
    message.hidden = !text;
    message.classList.add('is-visible');
  }

  syncMedia() {
    if (this.separateMode) {
      const v = this.config.variants.find(v => v.options[0] === this.activeFlavor);
      const description = this.querySelector('[data-linked-description]');
      if (description) { description.innerHTML = v?.description || ''; description.closest('details').hidden = !v?.description; }
      this.querySelectorAll('[data-linked-gallery]').forEach(host => {
        const show = String(v?.id) === host.dataset.linkedGallery;
        if (host.hidden === !show) return;
        host.hidden = !show;
        if (show) host.querySelector('[data-gallery]')?.dispatchEvent(new CustomEvent('gallery:refresh'));
        else { host.querySelectorAll('video').forEach(video => video.pause()); host.querySelectorAll('[data-external-media] iframe').forEach(frame => frame.remove()); host.querySelectorAll('[data-external-play]').forEach(button => button.hidden = false); }
      });
      this.querySelectorAll('[data-linked-nutrition]').forEach(link => {
        link.hidden = !v; if (!v) return;
        link.href = v.productUrl;
        if (v.nutritionKey) link.dataset.nutritionOpen = v.nutritionKey;
        else delete link.dataset.nutritionOpen;
      });
      return;
    }
    const items = [...this.querySelectorAll('[data-media-alt]')];
    const tagged = items.some((element) => /^flavor\s*:/i.test(element.dataset.mediaAlt || ''));

    items.forEach((element) => {
      if (!tagged) {
        element.hidden = false;
        return;
      }
      const alt = element.dataset.mediaAlt || '';
      const match = alt.match(/^flavor\s*:\s*([^|;]+)/i);
      if (!match) {
        element.hidden = false;
        return;
      }
      const flavors = match[1].split(',').map((value) => value.trim().toLowerCase());
      element.hidden = !flavors.includes(this.activeFlavor.toLowerCase());
    });
    this.querySelector('[data-gallery]')?.dispatchEvent(new CustomEvent('gallery:refresh'));
  }

  syncDynamicTiles() {
    const variant = this.selectedVariantForFlavor(this.activeFlavor) || this.config.variants.find((item) => String(item.id) === String(this.config.selectedVariantId));
    if (!variant) return;
    const desktopUrl = variant.flavorFeatureImage || variant.featuredImage || '';
    const mobileUrl = variant.flavorFeatureMobileImage || desktopUrl;
    this.querySelectorAll('[data-flavor-media-tile]').forEach((tile) => {
      const desktop = tile.querySelector('[data-flavor-media-desktop] img');
      const mobile = tile.querySelector('[data-flavor-media-mobile] img');
      if (desktop && desktopUrl) {
        if (!desktop.dataset.staticSrc) desktop.dataset.staticSrc = desktop.currentSrc || desktop.src;
        desktop.srcset = '';
        desktop.src = desktopUrl;
      }
      if (mobile && mobileUrl) {
        if (!mobile.dataset.staticSrc) mobile.dataset.staticSrc = mobile.currentSrc || mobile.src;
        mobile.srcset = '';
        mobile.src = mobileUrl;
      }
    });
  }

  syncAddState() {
    if (this.adding) {
      this.querySelectorAll('[data-composer-add], [data-mobile-add]').forEach(element => { element.disabled = true; });
      return;
    }
    const empty = this.totalQty() <= 0;
    const disabled = empty || this.connectionError || (this.config.requiresSellingPlan && !this.planId) || (this.separateMode && this.selectedLines().reduce((n,l)=>n+l.quantity,0) !== this.totalQty());
    this.querySelectorAll('[data-composer-add], [data-mobile-add]').forEach((button) => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.textContent = empty ? this.preAddLabel : this.addLabel;
    });
  }

  syncNutrition() {
    const container = this.querySelector('[data-nutrition-highlights][data-nutrition-source="automatic"]');
    const active = this.config.variants.find(variant => variant.options[this.flavorIndex] === this.activeFlavor && Object.entries(this.optionSelections).every(([index,value]) => Number(index) === this.flavorIndex || variant.options[Number(index)] === value)) || this.config.variants.find(variant => String(variant.id) === String(this.config.selectedVariantId));
    this.querySelectorAll('[data-nutrition-open], [data-linked-nutrition]').forEach(link => {
      if (!active) return;
      link.dataset.nutritionProduct = String(active.productId || this.dataset.productId || '');
      if (active.nutritionKey) link.dataset.nutritionOpen = active.nutritionKey;
      else if (window.BellyfulFacts?.enabled) link.dataset.nutritionOpen = '';
    });
    if (!container) return;
    if (window.BellyfulFacts?.enabled && ['idle','loading'].includes(window.BellyfulFacts.status)) {
      container.hidden = true;
      const caption = this.querySelector('[data-nutrition-basis]'); if (caption) caption.hidden = true;
      return;
    }
    const basis = this.querySelector('[data-nutrition-basis]');
    const selected = this.selectedLines().map(line => line.variant);
    // With no flavors chosen, represent every option compatible with the other selectors.
    const variants = selected.length ? selected : this.config.variants.filter(variant =>
      Object.entries(this.optionSelections).every(([index, value]) => Number(index) === this.flavorIndex || variant.options[Number(index)] === value)
    );
    const shared = window.BellyfulFacts?.status === 'ready';
    const facts = variants.map(variant => (shared ? (window.BellyfulFacts.get(variant.nutritionKey,variant.productId || this.dataset.productId)?.highlights || []) : (variant.nutrition?.highlights || [])).filter(item => item && item.value !== null && item.value !== undefined && String(item.value).trim() !== '' && item.label));
    // Single-option products may use their existing product-level nutrition profile.
    if (!shared && this.flavorIndex < 0 && !facts.some(items => items.length)) { container.hidden = false; if (basis) basis.hidden = false; return; }
    const normalize = label => String(label).trim().toLocaleLowerCase();
    const first = facts[0] || [];
    let rangeShown = false;
    const combined = first.map(item => {
      const matches = facts.map(items => items.find(other => normalize(other.label) === normalize(item.label)));
      if (matches.some(match => !match)) return null;
      const values = matches.map(match => String(match.value).trim());
      if (values.every(value => value === values[0])) return {value:values[0],label:item.label};
      const parsed = values.map(value => value.match(/^(\d+(?:\.\d+)?)\s*([a-z%]*)$/i));
      if (parsed.some(value => !value) || parsed.some(value => value[2].toLowerCase() !== parsed[0][2].toLowerCase())) return null;
      const numbers = parsed.map(value => Number(value[1]));
      const low = Math.min(...numbers), high = Math.max(...numbers);
      rangeShown = rangeShown || low !== high;
      return {value: `${low}${low === high ? '' : `–${high}`}${parsed[0][2]}`,label:item.label};
    }).filter(Boolean).slice(0, Number(container.dataset.nutritionLimit || 3));
    const cards = combined.map((item, index) => {
      const card = document.createElement('div');
      card.dataset.nutritionSlot = String(index + 1);
      const value = document.createElement('strong'), label = document.createElement('span');
      value.textContent = item.value; label.textContent = item.label;
      card.append(value,label); return card;
    });
    container.replaceChildren(...cards);
    container.hidden = cards.length === 0;
    if (basis) {
      basis.hidden = cards.length === 0;
      basis.textContent = rangeShown ? container.dataset.basisMixed : container.dataset.basisSingle;
    }
  }

  syncSummary() {
    const quantity = this.totalQty();
    const selectedLines = this.selectedLines();
    const servings = this.separateMode ? (selectedLines.length && selectedLines.every(l => l.variant.servings > 0) ? selectedLines.reduce((n,l)=>n+l.quantity*l.variant.servings,0) : 0) : quantity * this.servings;
    const hasServings = this.separateMode ? servings > 0 : this.servings > 0;
    const total = this.totalPrice();
    const title = this.querySelector('[data-summary-title]');
    const detail = this.querySelector('[data-summary-detail]');
    const tier = this.querySelector('[data-tier-label]');
    const price = this.querySelector('[data-total-price]');
    const perServing = this.querySelector('[data-per-serving]');
    const mobilePrice = this.querySelector('[data-mobile-price]');
    const mobileSummary = this.querySelector('[data-mobile-summary]');

    if (title) title.textContent = `${quantity} ${quantity === 1 ? this.unitSingular : this.unitPlural}`;
    if (detail) detail.textContent = hasServings ? `${servings} servings` : '';
    if (tier) tier.textContent = quantity >= this.tierThree ? this.tierLabels[2] : quantity >= this.tierTwo ? this.tierLabels[1] : this.tierLabels[0];
    if (price) price.textContent = this.format(total);
    if (mobilePrice) mobilePrice.textContent = this.format(total);
    if (mobileSummary) mobileSummary.textContent = `${quantity} ${quantity === 1 ? this.unitSingular : this.unitPlural}${hasServings ? ` · ${servings} servings` : ''}`;
    if (perServing) perServing.textContent = hasServings && servings ? `${this.format(Math.round(total / servings))} / serving` : '';

    const lines = this.querySelector('[data-summary-lines]');
    if (lines) {
      lines.replaceChildren();
      this.selectedLines().forEach((line) => {
        const row = document.createElement('div');
        const label = document.createElement('span');
        label.textContent = this.flavorIndex >= 0 ? line.flavor : this.unitSingular;
        const value = document.createElement('strong');
        value.textContent = `× ${line.quantity}`;
        row.append(label, value);
        lines.appendChild(row);
      });
    }
  }

  syncPurchase() {
    const empty = this.totalQty() <= 0;
    const common = this.commonPlans();
    const plans = this.querySelector('[data-selling-plans]');
    if (plans) plans.hidden = empty || this.purchaseType !== 'subscription' || !this.planId;

    const status = this.querySelector('[data-purchase-status]');
    const helper = this.querySelector('[data-purchase-helper]');
    if (status) status.textContent = empty ? 'Waiting for item' : (this.purchaseType === 'subscription' ? 'Subscription' : 'One-time');
    if (helper) {
      helper.hidden = !empty;
      if (empty) helper.textContent = this.purchaseHelperText;
    }

    this.querySelectorAll('[name^="purchase_type_"]').forEach((radio) => {
      radio.checked = !empty && radio.value === this.purchaseType;
      radio.setAttribute('aria-disabled', String(empty));
      if (radio.value === 'onetime') radio.closest('label').hidden = Boolean(this.config.requiresSellingPlan);
      if (radio.value === 'subscription') radio.disabled = !empty && !this.planId;
      radio.closest('label')?.classList.toggle('is-waiting', empty);
    });

    this.querySelectorAll('[data-selling-plan-id]').forEach((button) => {
      const valid = common.includes(String(button.dataset.sellingPlanId));
      button.hidden = !valid;
      if (!button.dataset.labelReady) {
        let label = button.dataset.deliveryLabel || button.textContent.trim();
        const group = button.dataset.planGroup || '';
        if (group && label.startsWith(group) && /^[\s,;:—–-]/.test(label.slice(group.length))) label = label.slice(group.length).replace(/^[\s,;:—–-]+/, '');
        label = window.BellyfulPlanLabel ? window.BellyfulPlanLabel(label) : label;
        button.textContent = label;
        button.dataset.labelReady = 'true';
      }
      button.setAttribute('aria-pressed', String(String(button.dataset.sellingPlanId) === String(this.planId)));
    });

    const oneTime = this.oneTimeTotal();
    const subscription = this.planId ? this.priceForLines(this.selectedLines(), true) : 0;

    const oneTimePrice = this.querySelector('[data-onetime-price]');
    const subscriptionPrice = this.querySelector('[data-subscription-price]');
    const savings = this.querySelector('[data-subscription-savings]');

    if (oneTimePrice) oneTimePrice.textContent = this.format(oneTime);
    if (subscriptionPrice) { subscriptionPrice.hidden = empty; subscriptionPrice.textContent = empty ? '' : (this.planId ? this.format(subscription) : 'Not available'); }
    const savingsBaseline = oneTime;
    const original = this.querySelector('[data-subscription-original]');
    if (original) { original.hidden = !this.planId || subscription >= savingsBaseline; original.textContent = this.format(savingsBaseline); }
    if (savings) {
      const amount = savingsBaseline - subscription;
      const percent = savingsBaseline > 0 && amount > 0 ? Math.round((amount / savingsBaseline) * 100) : 0;
      savings.hidden = !this.planId || !percent || this.dataset.showSubscriptionSavings === 'false';
      savings.textContent = this.planId && percent ? (this.dataset.subscriptionSavingLabel || 'Extra {percent}%').replace('{percent}', percent) : '';
    }
    this.querySelectorAll('[data-subscription-benefit-saving]').forEach((element) => {
      const amount = Math.max(0, savingsBaseline - subscription);
      element.textContent = amount > 0 ? `Save ${this.format(amount)} on this delivery` : 'See your subscription savings above';
    });
  }


  resetAfterAdd() {
    const flavors = Object.keys(this.quantities);
    flavors.forEach((flavor) => { this.quantities[flavor] = 0; });
    let resetFlavor = '';
    const selected = this.config.variants.find((variant) => String(variant.id) === String(this.config.selectedVariantId));
    if (selected && this.flavorIndex >= 0) resetFlavor = selected.options[this.flavorIndex] || '';
    if (!resetFlavor || !(resetFlavor in this.quantities)) resetFlavor = flavors.find((flavor) => {
      const row = this.querySelector(`[data-flavor-row][data-flavor="${CSS.escape(flavor)}"]`);
      return row?.dataset.available !== 'false';
    }) || flavors[0] || '';
    if (resetFlavor) {
      this.activeFlavor = resetFlavor;
      if (this.flavorIndex < 0) this.quantities[resetFlavor] = 1;
    }
    this.sync();
  }

  async addToCart(button) {
    if (this.adding || window.BellyfulTheme?.cartUpdating) return;
    const lines = this.selectedLines();
    const message = this.querySelector('[data-composer-message]');
    if (this.connectionError) { if (message) message.textContent = 'Flavor connections need review before ordering.'; return; }
    if (this.separateMode && this.purchaseType === 'subscription' && (!this.planId || lines.some(l => !this.allocation(l.variant)))) { if (message) message.textContent = 'Choose a subscription available for every selected flavor.'; return; }
    if (this.config.requiresSellingPlan && !this.planId) { if (message) message.textContent = 'No subscription plan is available for this selection.'; return; }
    if (this.totalQty() <= 0) {
      if (message) message.textContent = this.flavorIndex >= 0 ? 'Choose a flavor quantity first.' : 'Choose a quantity first.';
      this.syncAddState();
      return;
    }
    if (!lines.length || lines.reduce((sum,line) => sum + line.quantity, 0) !== this.totalQty()) {
      if (message) message.textContent = 'One or more selected options are unavailable. Please update your selection.';
      return;
    }

    this.adding = true;
    if (window.BellyfulTheme) window.BellyfulTheme.cartUpdating = true;
    const buttons = this.querySelectorAll('[data-composer-add], [data-mobile-add]');
    buttons.forEach((element) => { element.disabled = true; });
    if (message) message.textContent = 'Adding your routine…';

    const items = lines.map((line) => {
      const item = { id: line.variant.id, quantity: line.quantity };
      if (this.purchaseType === 'subscription' && this.planId) item.selling_plan = Number(this.separateMode ? this.allocation(line.variant).realId : this.planId);
      return item;
    });

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add to bag.');
      if (window.BellyfulTheme?.prepareCartCelebration) window.BellyfulTheme.prepareCartCelebration();
      if (message) message.textContent = 'Added to your routine ;b';
      this.resetAfterAdd();
      try { if (window.BellyfulRefreshCart) await window.BellyfulRefreshCart(true); } catch (_) { window.location.assign(`${root}cart`); }
    } catch (error) {
      // A failed add response can still leave an item in Shopify's cart (for
      // example a stock-limited partial add). Read back; never blindly resubmit.
      if (window.BellyfulTheme) {
        window.BellyfulTheme.cartCelebratePending = false;
        window.BellyfulTheme.cartCelebrationContext = null;
      }
      let refreshed = false;
      try {
        if (window.BellyfulRefreshCart) {
          await window.BellyfulRefreshCart(false);
          refreshed = true;
        }
      } catch (_) { /* Keep the customer's selected items available for review. */ }
      if (message) message.textContent = refreshed
        ? `${error.message || 'We could not confirm that change.'} Your bag has been refreshed. Check it before adding again.`
        : 'We could not confirm that change. Check your bag before adding again.';
    } finally {
      this.adding = false;
      if (window.BellyfulTheme) window.BellyfulTheme.cartUpdating = false;
      this.syncAddState();
    }
  }
}

if (!customElements.get('product-composer')) customElements.define('product-composer', ProductComposer);
